import i18next from "@app/i18n";

const enResources = {
    page_title: "Objectives",
    page_subtitle:
        "Manage your company’s objectives and targets for each  sustainability topic.",
    create_objective: "New objective",
    create_objective_short: "New",
    generate_with_ai: "Generate with Ai",
    toast_success: "Objective created successfully",
    toast_error_entity: "Please select an entity",
    toast_error_theme: "Please select a theme",
    toast_error_unknown: "Failed to create objective",
    empty_state_title: "Objectives",
    empty_state_subtitle: `You have not defined any objective. Setting measurable, 
        ambitious and realistic goals for each sustainability topic is a
        crucial step in building a CSR strategy.`,
    empty_state_button: "Create objective",
    no_objectives_found_title: "No objectives found",
    no_objectives_found_subtitle:
        "Try changing some parameters or adding objectives",
    work_plan: "Work plan",
    list: "Objectives list",
    breadcrumb_objectives: "Objectives",
    create_objective_tooltip:
        "You are a contributor and cannot create objectives",

    theme_with_no_subthemes: "{{theme}} - No subtheme",
    custom_theme: "Custom theme",
    ditto_guidance_title: "Enter EcoVadis Guidance View",
    ditto_guidance_subtitle:
        "View Ditto’s expert advice on how to best complete Objectives for EcoVadis.",
    ditto_guidance_select_placeholder: "Framework",
    ditto_guidance_exit: "Exit guidance view",
    ditto_guidance_ecovadis_title: "You're in EcoVadis Guidance View",
    ditto_guidance_button: "Enter guidance view",
    filter_change_confirmation_title:
        "Change filters from EcoVadis' required subthemes?",
    filter_change_confirmation_body: "This will exit EcoVadis Guidance View.",
    filter_change_confirmation_cancel: "Cancel",
    filter_change_confirmation_confirm: "Confirm",
    ditto_guidance_banner_title: "Ditto Guidance",
    ditto_guidance_banner_description:
        "We've filtered your Objectives by EcoVadis' required subthemes that are relevant to your business. We recommend validating 3 Objectives for each required subtheme.",
    subtheme_progress:
        "{{validated}} of {{total}} validated Objectives recommended for EcoVadis",
    validated_objectives: "Validated objectives",
} as const;

type ResourcesKeys = keyof typeof enResources;

const frResources: Record<ResourcesKeys, string> = {
    page_title: "Engagements et objectifs",
    page_subtitle:
        "Définissez et gérez les objectifs de votre entreprise pour chaque enjeu de durabilité.",
    create_objective: "Nouvel objectif",
    create_objective_short: "Nouveau",
    generate_with_ai: "Générer avec IA",
    toast_success: "Objectif créé avec succès",
    toast_error_entity: "Veuillez sélectionner une entité",
    toast_error_theme: "Veuillez sélectionner un thème",
    toast_error_unknown: "Impossible de créer l'objectif",
    empty_state_title: "Objectifs",
    empty_state_subtitle: `Vous n’avez pas encore défini d’objectif. Fixer des objectifs quantifiables, 
        ambitieux et réalistes pour chaque enjeu de durabilité constitue une étape
        essentielle pour bâtir une stratégie RSE.`,
    empty_state_button: "Créer un objectif",
    no_objectives_found_title: "Aucun objectif trouvé",
    no_objectives_found_subtitle:
        "Essayez de modifier certains paramètres ou d'ajouter des objectifs",
    work_plan: "Plan de travail",
    list: "Liste d'objectifs",
    breadcrumb_objectives: "Objectifs",
    create_objective_tooltip:
        "Vous êtes un contributeur et ne pouvez pas créer des objectifs",
    theme_with_no_subthemes: "{{theme}} - Aucun sous-thème",
    custom_theme: "Thème personnalisé",
    ditto_guidance_title: "Afficher l'aide EcoVadis",
    ditto_guidance_subtitle:
        "Voir les conseils experts de Ditto sur la meilleure façon de compléter les objectifs pour EcoVadis.",
    ditto_guidance_select_placeholder: "Référentiel",
    ditto_guidance_exit: "Quitter la vue",
    ditto_guidance_ecovadis_title: "Vous êtes dans l'aide EcoVadis",
    ditto_guidance_button: "Afficher l'aide",
    filter_change_confirmation_title:
        "Modifier les filtres des sous-thèmes requis d'EcoVadis ?",
    filter_change_confirmation_body:
        "Cela vous fera quitter la vue d'aide EcoVadis.",
    filter_change_confirmation_cancel: "Annuler",
    filter_change_confirmation_confirm: "Confirmer",
    ditto_guidance_banner_title: "Aide Ditto",
    ditto_guidance_banner_description:
        "Nous avons filtré vos Objectifs selon les sous-thèmes requis d'EcoVadis qui sont pertinents pour votre entreprise. Nous recommandons de valider 3 Objectifs pour chaque sous-thème requis.",
    subtheme_progress:
        "{{validated}} objectifs validés recommandés pour EcoVadis",
    validated_objectives: "Objectifs validés",
} as const;

export const keys = Object.fromEntries(
    Object.keys(enResources).map((key) => [key, key]),
) as Record<ResourcesKeys, ResourcesKeys>;

export const RESOURCE_NAME = "ObjectivePage";

i18next.addResourceBundle("en", RESOURCE_NAME, enResources);
i18next.addResourceBundle("fr", RESOURCE_NAME, frResources);
